import Fragment from "./fragment";
import FragmentDataType from "../enums/fragment-data-type";
import { playerReducer } from "../../../storage/reducers";
import FragmentSummary from "./fragment-summary";
import { FragmentMessages } from "../../common/fragment-messages";
import PenaltyAcceptedFieldPositionRestrictedPositions from "./restricted-positions/penalty-accepted-field-position-restricted-positions";
import FootballState from "../state/football-state";
import DownType from "../enums/down-type";
import { getFieldDirectionMultiplier } from "../utils/context-utils";

const FLAG_PENALTY_DEFAULTS = {
    FG: { yards: 5, downType: DownType.LossOfDownOnPLay },
    SC: { yards: 10, downType: DownType.Normal },
    DV: { yards: 5, downType: DownType.Normal },
    JMP: { yards: 5, downType: DownType.Normal },
    IRX: { yards: 5, downType: DownType.AutomaticFirstDown },
    TCK: { yards: 10, downType: DownType.AutomaticFirstDown }
};

const PENALTY_STATES = [
    FootballState.Penalty,
    FootballState.SecondPenalty,
    FootballState.PlayPenalty,
    FootballState.PlayPenaltySecondPenalty,
    FootballState.KickOffPenalty
];

export default class PenaltyAccepted extends Fragment {
    constructor(causedBy, enforcementSpot, fieldPosition, downType) {
        super();
        this.code = "A";
        this.causedBy = causedBy;
        this.enforcementSpot = enforcementSpot;
        this.fieldPosition = fieldPosition;
        this.downType = downType;

        this.requiredData = [
            {
                name: "causedBy",
                type: FragmentDataType.Player,
                displayName: "Caused by",
                title: (team) => `Caused By: ${team.teamName}`,
                subtitle: (team) =>
                    `Select a player from ${team.teamName} or enter the roster number`,
                isEditable: true
            },
            {
                name: "enforcementSpot",
                type: FragmentDataType.FieldPosition,
                displayName: "Enforcement spot",
                title: "Enforcement spot",
                subtitle: FragmentMessages.FB_FIELD_POSITION
            },
            {
                name: "fieldPosition",
                type: FragmentDataType.FieldPosition,
                displayName: "Field position",
                title: "Field position",
                subtitle: FragmentMessages.FB_TEAM_FIELD_POSITION,
                restrictedPositions: (props) => {
                    return new PenaltyAcceptedFieldPositionRestrictedPositions(
                        props
                    ).resolve();
                },
                defaultValue: ({ play, summaryContext }) => {
                    const penaltyFragment = play
                        .getFragments(PENALTY_STATES)
                        .pop();
                    const code = penaltyFragment?.penaltyType?.code;
                    const defaults = FLAG_PENALTY_DEFAULTS[code];
                    if (!defaults || this.enforcementSpot == null)
                        return undefined;

                    const { yards } = defaults;
                    const multiplier =
                        getFieldDirectionMultiplier(summaryContext);
                    const penaltyTeamId = penaltyFragment.team?.id;
                    const offenseTeamId = summaryContext.homeTeamHasPossession
                        ? summaryContext.homeTeamId
                        : summaryContext.awayTeamId;
                    const isOffensivePenalty = penaltyTeamId === offenseTeamId;
                    const direction = isOffensivePenalty ? -1 : 1;

                    const fieldLength =
                        summaryContext.gameConfig?.general?.fieldLength || 100;
                    const result =
                        this.enforcementSpot + yards * multiplier * direction;
                    return Math.max(0, Math.min(fieldLength, result));
                }
            },
            {
                name: "downType",
                type: (play) =>
                    play.historyContains(FootballState.PatTry)
                        ? FragmentDataType.PatPenaltyDownType
                        : FragmentDataType.PenaltyDownType,
                displayName: "Down",
                title: "Down Result",
                defaultValue: ({ play }) => {
                    if (this._skipDownType) {
                        return DownType.AutomaticFirstDown;
                    }
                    const penaltyFragment = play
                        .getFragments(PENALTY_STATES)
                        .pop();
                    const code = penaltyFragment?.penaltyType?.code;
                    return FLAG_PENALTY_DEFAULTS[code]?.downType;
                }
            }
        ];
    }

    onBuild(playData) {
        if (playData?.play?.historyContains(FootballState.KickOff)) {
            this._skipDownType = true;
            if (this.downType == null) {
                this.downType = DownType.AutomaticFirstDown;
            }
        }
    }

    excludeRequiredData(rd) {
        return rd.name === "downType" && this._skipDownType === true;
    }

    getPlayers() {
        return [this.causedBy];
    }

    getFieldPosition() {
        return this.fieldPosition;
    }

    summarize(summaryContext, stringifier) {
        const message = `by ${stringifier.firstInitial(
            this.causedBy
        )}, ${stringifier.gain(
            summaryContext,
            this.enforcementSpot,
            this.fieldPosition
        )} Accepted`;

        const titleMessage =
            summaryContext.triggerKO && !summaryContext.isKickOffPlay
                ? `Kickoff from ${stringifier.niceFieldPosition(
                      summaryContext.gameConfig.general.fieldLength,
                      this.fieldPosition,
                      summaryContext.homeTeam?.abbrName,
                      summaryContext.awayTeam?.abbrName
                  )}`
                : summaryContext.triggerPAT
                ? "PAT: Penalty"
                : null;

        return new FragmentSummary(message, titleMessage);
    }

    updateSummaryContext(summaryContext) {
        if (this.fieldPosition || this.fieldPosition === 0) {
            summaryContext.fieldPosition = this.fieldPosition;
        }
        if (summaryContext.triggerKO || summaryContext.triggerPAT) {
            summaryContext.sopPrinted = true;
        }
    }

    toJSON() {
        return {
            ...super.toJSON(),
            causedBy: playerReducer(this.causedBy)
        };
    }
}
